import { formatNumber } from '../../lib/formatters.js';
import { BUY_TYPE_LABELS } from './constants.js';

interface BuyTypeRow {
  buyType: string;
  buttonClickedUsers: number;
  modalUsers: number;
  purchasedUsers: number;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 購入ファネルの「buy_type 別内訳」深掘り。buy_button_clicked の buy_type プロパティごとに ボタンクリック / モーダル表示 / 購入完了 / クリック→購入率を表示する。
 * @graph-connects formatNumber [calls] 数値フォーマット
 */
export function PurchaseBuyTypeBreakdown({ data }: { data: Record<string, unknown>[] }) {
  const rows = data as unknown as BuyTypeRow[];

  if (rows.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          データがありません
        </p>
      </div>
    );
  }

  return (
    <div className="card animate-fade-in-up">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
        購入タイプ別コンバージョン
      </h3>
      <p className="text-xs text-[var(--color-text-muted)] mb-4">
        buy_type は buy_button_clicked イベントのプロパティ。 buy_type は 1=PAST_ITEM, 2=DIFF_COLOR,
        3=NOW_ITEM, 4=RECOMMEND_EC, 5=PAST_ITEM_DIFF_COLOR, 6=EC_ITEM の6種類。
        期間内にデータがないタイプは表示されません。
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                Buy Type
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                ボタンクリック
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                モーダル表示
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                購入完了
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                クリック→購入率
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const buyType = row.buyType;
              const clicked = row.buttonClickedUsers;
              const modal = row.modalUsers;
              const purchased = row.purchasedUsers;
              const cvr = clicked > 0 ? Math.round((purchased / clicked) * 1000) / 10 : 0;

              return (
                <tr
                  key={buyType}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                >
                  <td className="py-2.5 px-3">
                    <span className="font-medium text-[var(--color-text-primary)]">{buyType}</span>
                    {BUY_TYPE_LABELS[buyType] && (
                      <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                        {BUY_TYPE_LABELS[buyType]}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-primary)]">
                    {formatNumber(clicked)}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-primary)]">
                    {formatNumber(modal)}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono font-medium text-[var(--color-accent-primary)]">
                    {formatNumber(purchased)}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-[var(--color-bg-hover)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--color-accent-primary)]"
                          style={{ width: `${String(Math.min(cvr, 100))}%` }}
                        />
                      </div>
                      <span className="text-[var(--color-text-primary)] font-mono text-xs w-12 text-right">
                        {String(cvr)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
